import { Axios, AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { type IAxiosClient } from './IAxiosClient';

export class AxiosClient implements IAxiosClient {
  private apiTokenAuthorizationInterceptor: number | null = null;
  private responseInterceptor: number | null = null;
  private _axios: Axios;

  constructor(axios: Axios) {
    this._axios = axios;
    this.setupResponseInterceptor();
  }

  private setupResponseInterceptor() {
    this._axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      (err: AxiosError) => {
        const _data = err.response?.data as { message?: string };

        return Promise.reject(_data.message ? new Error(_data.message) : err);
      }
    );
  }

  public setAuthToken(token: string) {
    if (this.apiTokenAuthorizationInterceptor !== null) {
      this._axios.interceptors.request.eject(this.apiTokenAuthorizationInterceptor);
    }
    if (this.responseInterceptor !== null) {
      this._axios.interceptors.response.eject(this.responseInterceptor);
    }

    this.apiTokenAuthorizationInterceptor = this._axios.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        config.headers.Authorization = `Bearer ${token || ''}`;

        return config;
      }
    );

    this.responseInterceptor = this._axios.interceptors.response.use(
      (response: AxiosResponse) => response,
      async (error: AxiosError) => {
        if (error?.response?.status === 401) {
          this.unsetAuthToken();
        }
        return Promise.reject(error);
      }
    );
  }

  public unsetAuthToken() {
    if (this.apiTokenAuthorizationInterceptor !== null) {
      this._axios.interceptors.request.eject(this.apiTokenAuthorizationInterceptor);
    }
  }

  public getAxiosInstance() {
    return this._axios;
  }

  public get(path: string) {
    return this._axios.get(path);
  }

  public post(path: string, data: unknown) {
    return this._axios.post(path, data);
  }
}
